/**
 * Risk Register — per-device live register of risks with cross-links to
 * documents (SOPs that mitigate), regulatory alerts (that might revisit),
 * and change controls. Complements the ISO 14971 workflow tool under
 * `/risk`; this page is where a QA lead ANSWERS "what risks does this
 * device have right now?" at a glance.
 *
 * Data flow:
 *   GET /api/devices  → device dropdown
 *   GET /api/risks?device_id=xxx  → risk list
 *   GET /api/risks/summary/overview  → residual heatmap + per-device counts
 *   POST /api/risks / PATCH /api/risks/:id / DELETE /api/risks/:id
 *   POST /api/risks/:id/link/{document|alert|change}
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import ESignatureDialog from "@/components/ESignatureDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShieldAlert, Plus, LinkIcon, Trash2, FileText, Bell, GitBranch, Sparkles, X, Pencil, Download, Mail,
} from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";

const LEVELS = ["Low", "Medium", "High"];
const STATES = ["Open", "Mitigated", "Accepted", "Closed"];

const EMPTY = {
  device_id: "",
  title: "",
  hazard: "",
  hazardous_situation: "",
  harm: "",
  severity: "Medium",
  probability: "Medium",
  initial_risk_level: "Medium",
  residual_risk_level: "Medium",
  controls: "",
  owner_email: "",
  state: "Open",
  notes: "",
};

const levelCls = (lvl) => (
  lvl === "High" ? "bg-red-100 text-red-800 border-red-300"
  : lvl === "Medium" ? "bg-amber-100 text-amber-800 border-amber-300"
  : lvl === "Low" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
  : "bg-slate-100 text-slate-700 border-slate-300"
);

const stateCls = (s) => (
  s === "Open" ? "bg-red-50 text-red-700 border-red-300"
  : s === "Mitigated" ? "bg-blue-50 text-blue-700 border-blue-300"
  : s === "Accepted" ? "bg-amber-50 text-amber-700 border-amber-300"
  : "bg-emerald-50 text-emerald-700 border-emerald-300"
);

export default function RiskRegister() {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [risks, setRisks] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState(EMPTY);
  const [linkOpen, setLinkOpen] = useState(null); // {risk, type} - "document" | "alert" | "change"
  const [linkOptions, setLinkOptions] = useState([]);
  const [selectedLink, setSelectedLink] = useState("");
  const [exporting, setExporting] = useState(false);
  const [signOpen, setSignOpen] = useState(null); // {scope: "all"|"device"} — trigger signed export flow
  // Sev × Prob filters — populated from the URL when the user arrives via
  // a heatmap-cell deep link. Nullable so we can clear without triggering
  // a full remount.
  const [sevFilter, setSevFilter] = useState(null);
  const [probFilter, setProbFilter] = useState(null);

  const isAdmin = (user?.roles || []).includes("Admin");

  const loadDevices = useCallback(async () => {
    try {
      const { data } = await api.get("/devices");
      setDevices(data);
      // Support ?device=xxx&severity=Y&probability=Z URL params — used by
      // the Device page heatmap-cell deep link.
      const params = new URLSearchParams(window.location.search);
      const urlDevice = params.get("device");
      const urlSev = params.get("severity");
      const urlProb = params.get("probability");
      if (["Low", "Medium", "High"].includes(urlSev)) setSevFilter(urlSev);
      if (["Low", "Medium", "High"].includes(urlProb)) setProbFilter(urlProb);
      if (data.length && !deviceId) {
        const pref = urlDevice && data.find((d) => d.project_id === urlDevice);
        setDeviceId((pref && pref.project_id) || data[0].project_id);
      }
    } catch (e) {
      logger.warn("devices load failed", e?.response?.status);
    }
  }, [deviceId]);

  const loadRisks = useCallback(async () => {
    if (!deviceId) { setRisks([]); return; }
    setLoading(true);
    try {
      // Push sev/prob filters into the backend query — no client-side filter
      // so the same endpoint powers audits and reports.
      const qs = new URLSearchParams({ device_id: deviceId });
      if (sevFilter) qs.set("severity", sevFilter);
      if (probFilter) qs.set("probability", probFilter);
      const { data } = await api.get(`/risks?${qs.toString()}`);
      setRisks(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Risks load failed");
    } finally {
      setLoading(false);
    }
  }, [deviceId, sevFilter, probFilter]);

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/risks/summary/overview");
      setOverview(data);
    } catch (e) {
      logger.warn("overview load failed", e?.response?.status);
    }
  }, []);

  useEffect(() => { loadDevices(); loadOverview(); }, [loadDevices, loadOverview]);
  useEffect(() => { loadRisks(); }, [loadRisks]);

  const openCreate = () => {
    setForm({ ...EMPTY, device_id: deviceId });
    setDialog({ open: true, editing: null });
  };

  const openEdit = (r) => {
    setForm({
      device_id: r.device_id,
      title: r.title || "",
      hazard: r.hazard || "",
      hazardous_situation: r.hazardous_situation || "",
      harm: r.harm || "",
      severity: r.severity || "Medium",
      probability: r.probability || "Medium",
      initial_risk_level: r.initial_risk_level || "Medium",
      residual_risk_level: r.residual_risk_level || "Medium",
      controls: r.controls || "",
      owner_email: r.owner_email || "",
      state: r.state || "Open",
      notes: r.notes || "",
    });
    setDialog({ open: true, editing: r.risk_id });
  };

  const save = async () => {
    if (!form.title || form.title.length < 4) {
      toast.error("Title must be at least 4 characters");
      return;
    }
    try {
      if (dialog.editing) {
        await api.patch(`/risks/${dialog.editing}`, form);
        toast.success("Risk updated");
      } else {
        await api.post("/risks", form);
        toast.success("Risk added to register");
      }
      setDialog({ open: false, editing: null });
      loadRisks();
      loadOverview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (r) => {
    if (!confirm(`Delete "${r.title}"? This unlinks it from ${(r.linked_document_ids || []).length} SOP(s), ${(r.linked_alert_ids || []).length} alert(s), ${(r.linked_change_ids || []).length} change(s).`)) return;
    try {
      await api.delete(`/risks/${r.risk_id}`);
      toast.success("Risk deleted");
      loadRisks();
      loadOverview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const openLink = async (risk, type) => {
    setLinkOpen({ risk, type });
    setSelectedLink("");
    try {
      const endpoint = type === "document" ? "/documents"
        : type === "alert" ? "/alerts"
        : "/changes";
      const { data } = await api.get(endpoint);
      setLinkOptions(data);
    } catch (e) {
      toast.error(`Could not load ${type}s`);
    }
  };

  const commitLink = async () => {
    if (!selectedLink) return;
    try {
      await api.post(`/risks/${linkOpen.risk.risk_id}/link/${linkOpen.type}`,
        { entity_id: selectedLink });
      toast.success(`Linked ${linkOpen.type}`);
      setLinkOpen(null);
      loadRisks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed");
    }
  };

  const unlink = async (risk, type, entityId) => {
    try {
      await api.delete(`/risks/${risk.risk_id}/link/${type}/${entityId}`);
      loadRisks();
    } catch (e) {
      toast.error("Unlink failed");
    }
  };

  const exportBinder = async (scope, signatureId = null, emailSignatory = false) => {
    // scope: "device" → filter by current deviceId; anything else → all devices.
    // signatureId (optional): a valid e-signature turns the binder into a
    // signed Part 11 document with a signature block on the cover page.
    // emailSignatory (optional): also deliver the PDF to the workspace
    // signatory email (workspace_settings.signatory_email).
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (scope === "device" && deviceId) params.set("device_id", deviceId);
      if (signatureId) params.set("signature_id", signatureId);
      if (emailSignatory) params.set("email_signatory", "true");
      const url = `/risks/export/pdf${params.toString() ? `?${params.toString()}` : ""}`;
      const resp = await api.get(url, { responseType: "blob", timeout: 45000 });
      const eventId = resp.headers["x-audit-event-id"] || resp.headers["X-Audit-Event-Id"];
      const emailedTo = resp.headers["x-emailed-to"] || resp.headers["X-Emailed-To"];
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const stem = scope === "device" ? `risk_register_${deviceId}` : "risk_register";
      link.download = `${stem}${signatureId ? "_signed" : ""}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      const emailLine = emailedTo ? ` · emailed to ${emailedTo}` : "";
      toast.success(
        `${signatureId ? "Signed b" : "B"}inder exported${eventId ? ` · event ${eventId}` : ""}${emailLine}`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const deviceOptions = useMemo(() => devices.map((d) => (
    <SelectItem key={d.project_id} value={d.project_id}>{d.device_name || d.project_id}</SelectItem>
  )), [devices]);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6" data-testid="risk-register-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="vq-overline">ISO 14971 Register</div>
            <h1 className="font-display font-bold text-3xl text-slate-900 flex items-center gap-2">
              <ShieldAlert className="w-7 h-7 text-[#0055FF]" /> Risk Register
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              A live per-device register of risks. Snap each risk onto the SOPs that mitigate it,
              the regulatory alerts that might revisit it, and the change controls that touch it.
              Complements the ISO 14971 workflow tool at <a href="/risk" className="text-[#0055FF] hover:underline">/risk</a>.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  onClick={() => exportBinder("all")}
                  disabled={exporting}
                  data-testid="risk-export-all-btn"
                  className="border-slate-300 font-semibold"
                  title="Signed PDF binder of the full risk register (all devices). Every export is audit-logged."
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  {exporting ? "Exporting…" : "Export register"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setSignOpen({ scope: "all" })}
                  disabled={exporting}
                  data-testid="risk-export-signed-btn"
                  className="border-[#0055FF] text-[#0055FF] font-semibold hover:bg-[#0055FF] hover:text-white"
                  title="21 CFR Part 11 signed PDF binder — sign the export first; signature block prints on the cover."
                >
                  <Sparkles className="w-4 h-4 mr-1.5" />
                  Sign & export
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setSignOpen({ scope: "all", emailAfterSign: true })}
                  disabled={exporting}
                  data-testid="risk-export-sign-email-btn"
                  className="border-emerald-600 text-emerald-700 font-semibold hover:bg-emerald-600 hover:text-white"
                  title="Sign, export and email the signed binder to the workspace signatory in one shot."
                >
                  <Mail className="w-4 h-4 mr-1.5" />
                  Sign, email & export
                </Button>
                {deviceId && (
                  <Button
                    variant="outline"
                    onClick={() => exportBinder("device")}
                    disabled={exporting}
                    data-testid="risk-export-device-btn"
                    className="border-slate-300 font-semibold"
                    title="Signed PDF binder of just this device's risks. Every export is audit-logged."
                  >
                    <Download className="w-4 h-4 mr-1.5" />
                    Export this device
                  </Button>
                )}
              </>
            )}
            <Button
              onClick={openCreate}
              disabled={!deviceId}
              data-testid="risk-add-btn"
              className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add risk
            </Button>
          </div>
        </div>

        {overview && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="risk-overview">
            <div className="vq-card p-4">
              <div className="vq-overline">Total risks</div>
              <div className="font-display font-bold text-3xl mt-1" data-testid="risk-overview-total">
                {overview.total_risks}
              </div>
            </div>
            {["Low", "Medium", "High"].map((lvl) => (
              <div key={lvl} className="vq-card p-4">
                <div className="vq-overline">Residual · {lvl}</div>
                <div className={`inline-block font-display font-bold text-3xl mt-1 px-2 rounded ${levelCls(lvl)}`}>
                  {overview.residual_risk_distribution?.[lvl] || 0}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="vq-card p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-xs font-semibold" htmlFor="risk-device-select">Device</Label>
            <div className="min-w-[240px]">
              <Select value={deviceId} onValueChange={setDeviceId}>
                <SelectTrigger data-testid="risk-device-select" id="risk-device-select">
                  <SelectValue placeholder="Select device" />
                </SelectTrigger>
                <SelectContent>{deviceOptions}</SelectContent>
              </Select>
            </div>
            <div className="text-xs text-slate-500 font-mono">
              {loading ? "Loading…" : `${risks.length} risk${risks.length === 1 ? "" : "s"} on this device`}
            </div>
            {(sevFilter || probFilter) && (
              <div
                data-testid="risk-active-filters"
                className="flex items-center gap-1.5 text-xs bg-[#0055FF]/10 border border-[#0055FF]/30 text-[#0055FF] font-mono px-2.5 py-1 rounded"
              >
                <span>Filter:</span>
                {sevFilter && <span data-testid="risk-active-sev">Sev · {sevFilter}</span>}
                {sevFilter && probFilter && <span>·</span>}
                {probFilter && <span data-testid="risk-active-prob">Prob · {probFilter}</span>}
                <button
                  data-testid="risk-clear-filters"
                  onClick={() => {
                    setSevFilter(null);
                    setProbFilter(null);
                    // Also strip from the URL so refresh keeps the clear state.
                    const url = new URL(window.location.href);
                    url.searchParams.delete("severity");
                    url.searchParams.delete("probability");
                    window.history.replaceState({}, "", url.toString());
                  }}
                  className="ml-1 hover:text-slate-900"
                  title="Clear Sev × Prob filters"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </div>

        {!deviceId ? (
          <div className="vq-card p-8 text-center text-slate-500">
            No devices yet. Add a device under <a href="/devices" className="text-[#0055FF] hover:underline">Devices</a> to start a risk register.
          </div>
        ) : risks.length === 0 && !loading ? (
          <div className="vq-card p-8 text-center text-slate-500" data-testid="risk-empty">
            No risks logged for this device yet. Click <b>Add risk</b> to start the register.
          </div>
        ) : (
          <div className="space-y-3">
            {risks.map((r) => (
              <RiskCard
                key={r.risk_id}
                risk={r}
                onEdit={() => openEdit(r)}
                onDelete={() => remove(r)}
                onLink={(t) => openLink(r, t)}
                onUnlink={(t, id) => unlink(r, t, id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="risk-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {dialog.editing ? "Edit risk" : "Add risk to register"}
            </DialogTitle>
            <DialogDescription>
              ISO 14971 vocabulary — hazard, hazardous situation, harm. Assign a severity + probability,
              then estimate initial risk. Add controls, and update residual risk after verification.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold">Title</Label>
              <Input data-testid="risk-form-title" value={form.title}
                     onChange={(e) => setForm({ ...form, title: e.target.value })}
                     placeholder="Short summary — e.g. Occlusion during infusion" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Hazard</Label>
              <Input data-testid="risk-form-hazard" value={form.hazard}
                     onChange={(e) => setForm({ ...form, hazard: e.target.value })}
                     placeholder="e.g. Delivery of incorrect dose" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Harm</Label>
              <Input data-testid="risk-form-harm" value={form.harm}
                     onChange={(e) => setForm({ ...form, harm: e.target.value })}
                     placeholder="e.g. Hypoglycemia, patient injury" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold">Hazardous situation</Label>
              <Textarea data-testid="risk-form-situation" value={form.hazardous_situation}
                     onChange={(e) => setForm({ ...form, hazardous_situation: e.target.value })}
                     className="h-16" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Severity</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="risk-form-severity"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Probability</Label>
              <Select value={form.probability} onValueChange={(v) => setForm({ ...form, probability: v })}>
                <SelectTrigger data-testid="risk-form-probability"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Initial risk</Label>
              <Select value={form.initial_risk_level} onValueChange={(v) => setForm({ ...form, initial_risk_level: v })}>
                <SelectTrigger data-testid="risk-form-initial"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Residual risk (after controls)</Label>
              <Select value={form.residual_risk_level || "Medium"} onValueChange={(v) => setForm({ ...form, residual_risk_level: v })}>
                <SelectTrigger data-testid="risk-form-residual"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold">Risk controls (link SOPs below after saving)</Label>
              <Textarea data-testid="risk-form-controls" value={form.controls}
                     onChange={(e) => setForm({ ...form, controls: e.target.value })}
                     className="h-16" placeholder="Free-text summary of controls in place — mitigations, alarms, IFU statements, training." />
            </div>
            <div>
              <Label className="text-xs font-semibold">Owner email</Label>
              <Input data-testid="risk-form-owner" value={form.owner_email}
                     onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                     placeholder="qa@company.com" />
            </div>
            <div>
              <Label className="text-xs font-semibold">State</Label>
              <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                <SelectTrigger data-testid="risk-form-state"><SelectValue /></SelectTrigger>
                <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, editing: null })}>Cancel</Button>
            <Button data-testid="risk-save-btn" onClick={save} className="bg-slate-900 hover:bg-[#0055FF] text-white">
              {dialog.editing ? "Save changes" : "Add to register"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link dialog */}
      <Dialog open={!!linkOpen} onOpenChange={(o) => { if (!o) setLinkOpen(null); }}>
        <DialogContent data-testid="risk-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-display capitalize">Link {linkOpen?.type}</DialogTitle>
            <DialogDescription>
              Attach a {linkOpen?.type} to <b>{linkOpen?.risk?.title}</b>. Denormalized both ways so the {linkOpen?.type} page will show this risk too.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {linkOptions.map((opt) => {
              const id = opt.document_id || opt.alert_id || opt.change_id;
              const label = opt.title || id;
              return (
                <label key={id} className="flex items-center gap-2 p-2 border border-slate-200 rounded hover:bg-slate-50 cursor-pointer">
                  <input
                    type="radio"
                    name="link-target"
                    value={id}
                    checked={selectedLink === id}
                    onChange={() => setSelectedLink(id)}
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{label}</div>
                    <div className="text-[10px] font-mono text-slate-500">{id}</div>
                  </div>
                </label>
              );
            })}
            {linkOptions.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No {linkOpen?.type}s to link.</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(null)}>Cancel</Button>
            <Button data-testid="risk-link-commit" onClick={commitLink} disabled={!selectedLink}>Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Signed export flow — Admin signs, then we call export with the sig id.
          The scope object may also carry {emailAfterSign: true} to fan the
          signed binder out to the workspace signatory in one click. */}
      <ESignatureDialog
        open={!!signOpen}
        onOpenChange={(o) => { if (!o) setSignOpen(null); }}
        entityType="risk_register"
        entityId="binder"
        action="risk_register_export"
        defaultMeaning="Approval — Risk Register Binder Export"
        title="Sign Risk Register Export"
        description="21 CFR Part 11 · your signature will print on the binder cover along with the audit event ID."
        onSigned={(sig) => {
          const scope = signOpen?.scope || "all";
          const emailAfter = !!signOpen?.emailAfterSign;
          setSignOpen(null);
          exportBinder(scope, sig.signature_id, emailAfter);
        }}
      />
    </DashboardLayout>
  );
}

function RiskCard({ risk, onEdit, onDelete, onLink, onUnlink }) {
  const [expanded, setExpanded] = useState(false);
  const links = [
    { type: "document", ids: risk.linked_document_ids || [], icon: FileText, label: "SOPs" },
    { type: "alert", ids: risk.linked_alert_ids || [], icon: Bell, label: "Alerts" },
    { type: "change", ids: risk.linked_change_ids || [], icon: GitBranch, label: "Changes" },
  ];
  return (
    <div className="vq-card p-4" data-testid={`risk-card-${risk.risk_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Badge className={`text-[10px] font-mono uppercase border ${stateCls(risk.state)}`}>{risk.state}</Badge>
            <Badge className={`text-[10px] font-mono uppercase border ${levelCls(risk.residual_risk_level || risk.initial_risk_level)}`}>
              Residual · {risk.residual_risk_level || "unset"}
            </Badge>
            <span className="text-[10px] font-mono text-slate-500 uppercase">initial {risk.initial_risk_level}</span>
          </div>
          <div className="font-display font-bold text-slate-900" data-testid={`risk-title-${risk.risk_id}`}>{risk.title}</div>
          {risk.hazard && (
            <div className="text-xs text-slate-600 mt-1">
              <span className="font-mono uppercase text-[9px] tracking-wider text-slate-500">hazard · </span>
              {risk.hazard}
              {risk.harm && <> · <span className="font-mono uppercase text-[9px] tracking-wider text-slate-500">harm · </span>{risk.harm}</>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`risk-edit-${risk.risk_id}`} title="Edit">
            <Pencil className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`risk-delete-${risk.risk_id}`} title="Delete">
            <Trash2 className="w-4 h-4 text-red-600" />
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3 flex-wrap">
        {links.map((l) => (
          <button
            key={l.type}
            onClick={() => { setExpanded(true); onLink(l.type); }}
            data-testid={`risk-link-${l.type}-${risk.risk_id}`}
            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded border border-slate-200 hover:border-[#0055FF] hover:text-[#0055FF] text-slate-600"
          >
            <l.icon className="w-3 h-3" /> {l.label} · {l.ids.length}
            <Plus className="w-3 h-3" />
          </button>
        ))}
        {(links.some((l) => l.ids.length > 0)) && (
          <button
            onClick={() => setExpanded(!expanded)}
            data-testid={`risk-toggle-links-${risk.risk_id}`}
            className="text-[10px] font-mono uppercase tracking-wider text-[#0055FF] hover:underline"
          >
            {expanded ? "Hide" : "Show"} linked
          </button>
        )}
      </div>

      {expanded && (
        <div className="mt-3 border-t border-slate-100 pt-3 space-y-3" data-testid={`risk-linked-panel-${risk.risk_id}`}>
          <LinkList label="Linked SOPs" icon={FileText} items={(risk.linked_documents || []).map((d) => ({
            id: d.document_id, title: `${d.title} · v${d.version} · ${d.state}`, href: `/documents?doc=${d.document_id}`,
          }))} onUnlink={(id) => onUnlink("document", id)} testidPrefix={`risk-linked-doc-${risk.risk_id}`} />
          <LinkList label="Linked Alerts" icon={Bell} items={(risk.linked_alerts || []).map((a) => ({
            id: a.alert_id, title: `${a.source} — ${a.title}`, extra: a.ai_triage?.relevance_score ? `AI ${a.ai_triage.relevance_score}` : null, href: "/alerts",
          }))} onUnlink={(id) => onUnlink("alert", id)} testidPrefix={`risk-linked-alert-${risk.risk_id}`} />
          <LinkList label="Linked Changes" icon={GitBranch} items={(risk.linked_changes || []).map((c) => ({
            id: c.change_id, title: `${c.title} · ${c.state} · ${c.risk_level}`, href: "/changes",
          }))} onUnlink={(id) => onUnlink("change", id)} testidPrefix={`risk-linked-change-${risk.risk_id}`} />
        </div>
      )}
    </div>
  );
}

function LinkList({ label, icon: Icon, items, onUnlink, testidPrefix }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="space-y-1">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded px-2 py-1" data-testid={`${testidPrefix}-${it.id}`}>
            <a href={it.href} className="text-xs text-slate-800 truncate hover:text-[#0055FF]">{it.title}</a>
            <div className="flex items-center gap-2">
              {it.extra && <span className="text-[9px] font-mono text-[#0055FF] bg-[#0055FF]/10 border border-[#0055FF]/30 rounded px-1.5 py-0.5">
                <Sparkles className="w-2.5 h-2.5 inline" /> {it.extra}
              </span>}
              <button onClick={() => onUnlink(it.id)} title="Unlink" className="text-slate-400 hover:text-red-500">
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
